// Money, in paise.
//
// Every amount is an integer number of paise. Rupees as a float is how
// totals end up a paisa out and nobody can say why: 0.1 + 0.2 is
// 0.30000000000000004, and 13.33 * 1.05 is 13.996499999999999. Integers
// have none of that.
//
// The database stores numeric(12,2) and postgres-js returns it as a
// string, which is parsed here exactly once.

export const toPaise = (rupees: string | number): number =>
  Math.round(Number(rupees) * 100)

export const fromPaise = (paise: number): string => (paise / 100).toFixed(2)

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})

export const formatPaise = (paise: number): string => inr.format(paise / 100)

export type GstBreakdown = {
  base: number
  cgst: number
  sgst: number
  total: number
}

/**
 * Splits a taxable value into its CGST and SGST components.
 *
 * Each component is rounded SEPARATELY, because a GST invoice prints them
 * as separate lines and the total must equal the sum of what is printed.
 * Rounding the total instead can differ by a paisa -- 312.50 at 2.5 + 2.5
 * gives 328.12 this way and 328.13 the other -- and an invoice whose
 * figures do not add up is the one an inspector asks about.
 *
 * Intra-state supply only, which is the normal case here: a patient in
 * Rajasthan buying from a Rajasthan pharmacy. IGST would apply instead for
 * an out-of-state supply.
 */
export function gstBreakdown(basePaise: number, cgstRate: number, sgstRate: number): GstBreakdown {
  const cgst = Math.round((basePaise * cgstRate) / 100)
  const sgst = Math.round((basePaise * sgstRate) / 100)
  return { base: basePaise, cgst, sgst, total: basePaise + cgst + sgst }
}
